import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Eye, Trash2, Calendar, FileText, X } from 'lucide-react';
import ReactDOM from 'react-dom';

const ESTADO_FILTERS = [
    { id: 'pendiente', label: 'Pendientes' },
    { id: 'cerrada', label: 'Cerradas' },
    { id: 'todas', label: 'Todas' },
];

export default function PreventasPage() {
    const [preventas, setPreventas] = useState([]);
    const [estadoFilter, setEstadoFilter] = useState('pendiente');
    const [selectedPreventa, setSelectedPreventa] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPreventas(estadoFilter);
    }, [estadoFilter]);

    const fetchPreventas = async (estado = 'pendiente') => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/preventas?estado=${estado}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPreventas(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreventaDetails = async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedPreventa(data);
                setShowModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar detalles');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta preventa? Esta acción no se puede deshacer.')) return;
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setPreventas(prev => prev.filter(p => p.id !== id));
                if (selectedPreventa?.id === id) setShowModal(false);
            } else {
                alert('No se pudo eliminar la preventa');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);
    const formatDate = (dateString) => new Date(dateString).toLocaleString('es-CL');
    const isPendiente = (p) => p.estado === true;
    const pendientesCount = estadoFilter === 'pendiente' ? preventas.length : null;

    return (
        <div className="page p-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Gestión de Preventas</h1>
                    <p className="text-gray-500 mt-1">Administra preventas pendientes y consulta el historial de cerradas</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        {ESTADO_FILTERS.map(f => (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setEstadoFilter(f.id)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                    estadoFilter === f.id
                                        ? 'bg-white text-indigo-700 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    {pendientesCount != null && (
                        <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                            <FileText size={20} />
                            <span>Pendientes: {pendientesCount}</span>
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : preventas.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                    <div className="bg-gray-100 p-4 rounded-full inline-flex mb-4">
                        <FileText size={32} className="text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">
                        {estadoFilter === 'pendiente' && 'No hay preventas pendientes'}
                        {estadoFilter === 'cerrada' && 'No hay preventas cerradas'}
                        {estadoFilter === 'todas' && 'No hay preventas registradas'}
                    </h3>
                    <p className="text-gray-500 mt-1">
                        {estadoFilter === 'pendiente'
                            ? 'Las nuevas preventas generadas en POS aparecerán aquí.'
                            : 'Las preventas cobradas en caja quedan registradas como cerradas.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                                <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                                    <th className="p-4">ID</th>
                                    <th className="p-4">Estado</th>
                                    <th className="p-4">Origen</th>
                                    <th className="p-4">Vendedor</th>
                                    <th className="p-4">Fecha</th>
                                    <th className="p-4">Items</th>
                                    <th className="p-4 text-right">Total</th>
                                    {(estadoFilter === 'cerrada' || estadoFilter === 'todas') && (
                                        <th className="p-4">Venta</th>
                                    )}
                                    <th className="p-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {preventas.map(preventa => {
                                    const isOnline = preventa.origen === 'venta_online';
                                    return (
                                    <tr
                                        key={preventa.id}
                                        className={`hover:bg-gray-50 transition-colors group ${
                                            isOnline ? 'bg-emerald-50/30' : ''
                                        }`}
                                    >
                                        <td className="p-4 font-medium text-gray-900">#{preventa.id}</td>
                                        <td className="p-4">
                                            {isPendiente(preventa) ? (
                                                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">PENDIENTE</span>
                                            ) : (
                                                <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-semibold">CERRADA</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {isOnline ? (
                                                <span
                                                    className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold"
                                                    title={`Pagada online${preventa.payment_provider ? ` vía ${preventa.payment_provider}` : ''}${preventa.payment_ref ? ` · ref ${preventa.payment_ref}` : ''}`}
                                                >
                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                    VENTA ONLINE
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-500">POS</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-gray-600">
                                            {isOnline ? (
                                                <span className="text-emerald-700 italic">— ecommerce —</span>
                                            ) : (
                                                preventa.vendedor_name || 'Desconocido'
                                            )}
                                        </td>
                                        <td className="p-4 text-gray-500 flex items-center gap-2">
                                            <Calendar size={14} />
                                            {formatDate(preventa.created_at)}
                                        </td>
                                        <td className="p-4 text-gray-600">{preventa.cantidad_productos} productos</td>
                                        <td className="p-4 text-right font-bold text-gray-900 text-lg">
                                            {formatCurrency(preventa.sub_total)}
                                        </td>
                                        {(estadoFilter === 'cerrada' || estadoFilter === 'todas') && (
                                            <td className="p-4 text-gray-600">
                                                {preventa.venta_id ? `#${preventa.venta_id}` : '—'}
                                            </td>
                                        )}
                                        <td className="p-4">
                                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => fetchPreventaDetails(preventa.id)}
                                                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Ver Detalles"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                                {isPendiente(preventa) && (
                                                    <button
                                                        onClick={() => handleDelete(preventa.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showModal && selectedPreventa && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Preventa #{selectedPreventa.id}</h2>
                                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1 flex-wrap">
                                    {isPendiente(selectedPreventa) ? (
                                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">PENDIENTE</span>
                                    ) : (
                                        <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-semibold">CERRADA</span>
                                    )}
                                    {selectedPreventa.origen === 'venta_online' && (
                                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                            VENTA ONLINE
                                        </span>
                                    )}
                                    <span>• {formatDate(selectedPreventa.created_at)}</span>
                                    <span>
                                        • {selectedPreventa.origen === 'venta_online'
                                            ? 'Origen: Venta Online'
                                            : `Vendedor: ${selectedPreventa.vendedor_name || 'Desconocido'}`}
                                    </span>
                                    {selectedPreventa.venta_id && (
                                        <span>• Venta #{selectedPreventa.venta_id}</span>
                                    )}
                                </p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            {selectedPreventa.origen === 'venta_online' && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                        <h3 className="text-sm font-semibold text-emerald-900 uppercase tracking-wider">
                                            Venta online ya pagada
                                        </h3>
                                    </div>
                                    <p className="text-xs text-emerald-800/80 mb-3">
                                        El cliente pagó por la web — el cajero solo debe verificar y entregar/despachar.
                                        No vuelva a cobrar.
                                    </p>
                                    <dl className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <dt className="text-emerald-700 font-medium">Pasarela</dt>
                                            <dd className="text-emerald-900">
                                                {selectedPreventa.payment_provider || '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-emerald-700 font-medium">Estado pago</dt>
                                            <dd className="text-emerald-900">
                                                {selectedPreventa.payment_status || '—'}
                                            </dd>
                                        </div>
                                        <div className="col-span-2">
                                            <dt className="text-emerald-700 font-medium">Referencia</dt>
                                            <dd className="text-emerald-900 font-mono break-all">
                                                {selectedPreventa.payment_ref || '—'}
                                            </dd>
                                        </div>
                                    </dl>
                                    {selectedPreventa.nota && (
                                        <div className="mt-3 pt-3 border-t border-emerald-200">
                                            <dt className="text-emerald-700 font-medium text-xs mb-1">Detalle del pedido</dt>
                                            <pre className="whitespace-pre-wrap text-xs text-emerald-900 font-sans">
                                                {selectedPreventa.nota}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Resumen Financiero</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Neto</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(selectedPreventa.monto_neto)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">IVA (19%)</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(selectedPreventa.iva)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Total</p>
                                        <p className="font-bold text-indigo-600 text-lg">{formatCurrency(selectedPreventa.sub_total)}</p>
                                    </div>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-gray-800 mb-4">Productos</h3>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3">Código</th>
                                            <th className="px-4 py-3">Producto</th>
                                            <th className="px-4 py-3 text-right">Cantidad</th>
                                            <th className="px-4 py-3 text-right">Precio Unit.</th>
                                            <th className="px-4 py-3 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {selectedPreventa.items?.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.codigo_barra || '-'}</td>
                                                <td className="px-4 py-3 font-medium text-gray-800">
                                                    {item.promocion_id
                                                        ? (item.promocion_nombre ? `Promo: ${item.promocion_nombre}` : item.product_name)
                                                        : item.product_name}
                                                    {item.gramos ? ` (${item.gramos}g)` : ''}
                                                </td>
                                                <td className="px-4 py-3 text-right">{item.gramos ? `${item.gramos}g` : item.cantidad}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(item.precio_unitario)}</td>
                                                <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(item.precio_total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                                        <tr>
                                            <td colSpan="4" className="px-4 py-3 text-right">Total Final</td>
                                            <td className="px-4 py-3 text-right">{formatCurrency(selectedPreventa.sub_total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                            {isPendiente(selectedPreventa) && (
                                <button
                                    onClick={() => handleDelete(selectedPreventa.id)}
                                    className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash2 size={18} /> Eliminar Preventa
                                </button>
                            )}
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium transition-colors shadow-lg shadow-gray-200"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
